import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Wallet, Landmark, RefreshCw, Banknote } from 'lucide-react';

interface CashItem {
  id: string;
  hora: string;
  valor: number;
  forma: string;
  pagador?: string | null;
  contrato?: string | null;
  imovel?: string | null;
  observacao?: string | null;
}
interface Cashbox {
  date: string;
  caixaFisico: { itens: CashItem[]; total: number };
  conta: { itens: CashItem[]; total: number };
  total: number;
}

const FORMA_LABEL: Record<string, string> = {
  CASH: 'Dinheiro',
  CHECK: 'Cheque',
  PIX: 'PIX',
  BANK_TRANSFER: 'Depósito/Transf.',
  CREDIT_CARD: 'Cartão crédito',
  DEBIT_CARD: 'Cartão débito',
  BOLETO: 'Boleto',
  OTHER: 'Outros',
};

const money = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

export default function CaixaPage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<Cashbox | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const resp = await api.get('/finance/cashbox', { params: { date } });
      setData(resp.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const Section = ({
    title,
    icon: Icon,
    color,
    group,
  }: {
    title: string;
    icon: any;
    color: string;
    group?: { itens: CashItem[]; total: number };
  }) => (
    <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-center gap-2 font-semibold dark:text-dark-text-primary">
          <Icon className={`w-5 h-5 ${color}`} /> {title}
        </div>
        <div className={`font-bold ${color}`}>{money(group?.total || 0)}</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-dark-bg-tertiary text-left text-gray-500 dark:text-dark-text-secondary">
          <tr>
            <th className="px-4 py-2">Hora</th>
            <th className="px-4 py-2">Forma</th>
            <th className="px-4 py-2">Pagador / Contrato</th>
            <th className="px-4 py-2">Justificativa</th>
            <th className="px-4 py-2 text-right">Valor</th>
          </tr>
        </thead>
        <tbody>
          {(!group || group.itens.length === 0) && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                Nenhuma entrada.
              </td>
            </tr>
          )}
          {group?.itens.map((i) => (
            <tr key={i.id} className="border-t border-gray-100 dark:border-dark-border dark:text-dark-text-primary">
              <td className="px-4 py-2">{new Date(i.hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</td>
              <td className="px-4 py-2">{FORMA_LABEL[i.forma] || i.forma}</td>
              <td className="px-4 py-2">
                <div>{i.pagador || '—'}</div>
                <div className="text-xs text-gray-400">{i.contrato || ''}</div>
              </td>
              <td className="px-4 py-2 text-gray-500 dark:text-dark-text-secondary">{i.observacao || '—'}</td>
              <td className="px-4 py-2 text-right font-medium">{money(i.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold dark:text-dark-text-primary flex items-center gap-2">
          <Wallet className="w-6 h-6" /> Caixa
        </h1>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-gray-200 dark:border-dark-border rounded-lg px-3 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
          />
          <button onClick={load} className="p-2 text-gray-500 hover:text-gray-800" title="Atualizar">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <p className="text-sm text-emerald-700">Caixa físico (dinheiro/cheque)</p>
          <p className="text-2xl font-bold text-emerald-800">{money(data?.caixaFisico.total || 0)}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">Conta (PIX/transferência/cartão)</p>
          <p className="text-2xl font-bold text-blue-800">{money(data?.conta.total || 0)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Total do dia</p>
          <p className="text-2xl font-bold dark:text-dark-text-primary">{money(data?.total || 0)}</p>
        </div>
      </div>

      <Section title="Caixa físico — Dinheiro / Cheque" icon={Banknote} color="text-emerald-600" group={data?.caixaFisico} />
      <Section title="Conta bancária — PIX / Transferência / Cartão" icon={Landmark} color="text-blue-600" group={data?.conta} />
    </div>
  );
}
